import * as Sentry from '@sentry/react'
import { StoreProvider } from 'easy-peasy'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom'
import App from './App'
import { ObserveView } from './features/ObserveView/ObserveView'
import DailyPuzzleViewV2 from './features/Puzzles/DailyPuzzle/DailyPuzzleView'
import DailyPuzzleView from './features/PuzzlesView/DailyPuzzleView'

import { ThemeProvider } from '@mui/material'
import { RouteErrorBoundary } from './ErrorBoundary'
import AppError from './components/AppError/AppError'
import { ComponentsView } from './components/ComponentsView'
import { ProfileViewV2 } from './features/ProfileV2'
import { PuzzleQuest } from './features/PuzzleQuest/PuzzleQuestView'
import PuzzlePracticeView from './features/Puzzles/PuzzlePractice/PuzzlePracticeView'
import PuzzlesTrainingViewV2 from './features/PuzzlesView/PuzzlesTrainingViewV2'
import { AnalysisView } from './features/analysisView/AnalysisView'
import { GameView } from './features/gameView/GameView'
import { ENABLE_COMPONENTS, ENABLE_DAILY_PUZZLE_V2, ENABLE_PUZZLE_PRACTICE_V3, ENV, IS_DEV, IS_PROD } from './env'
import reportWebVitals from './reportWebVitals'
import { DialogsProvider } from './sharedComponents/src/Contexts/DialogsContext'
import theme from './sharedComponents/src/Theme/theme-v2'
import { setSharedErrorReporter } from './sharedComponents/src/utils/errorReporting'
import store from './store/store'
import {
    setSharedMetricReporter,
    setSharedTelemetryContextReporter,
} from './sharedComponents/src/utils/metricsReporting'

// React #11538: translators and DOM-mutating extensions move React-owned nodes, and React's commit then throws
// NotFoundError, which crashes the route. Skip the impossible DOM op instead and leave a breadcrumb.
const nodeRemoveChild = Node.prototype.removeChild
Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
        Sentry.addBreadcrumb({ category: 'dom', level: 'warning', message: 'removeChild: node is not a child' })
        return child
    }
    return nodeRemoveChild.call(this, child) as T
}
const nodeInsertBefore = Node.prototype.insertBefore
Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
        Sentry.addBreadcrumb({ category: 'dom', level: 'warning', message: 'insertBefore: reference is not a child' })
        return node
    }
    return nodeInsertBefore.call(this, node, child) as T
}

// A lazy chunk that fails to load after a deploy means this tab runs a build whose assets are gone. Reload once,
// only when '/' now names a different entry (not on a network blip). Never preventDefault: importers need the error.
window.addEventListener('vite:preloadError', () => {
    const entryRE = /\/assets\/index-[\w-]+\.js/
    const running = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src')
    if (!running) return
    fetch('/', { cache: 'no-store' })
        .then((res) => (res.ok ? res.text() : ''))
        .then((html) => {
            const deployed = html.match(entryRE)?.[0]
            if (!deployed || deployed === running.match(entryRE)?.[0]) return
            const last = Number(sessionStorage.getItem('preloadErrorReloadAt'))
            if (Date.now() - last < 10 * 60 * 1000) return
            sessionStorage.setItem('preloadErrorReloadAt', String(Date.now()))
            window.location.reload()
        })
        .catch(() => {})
})

// Sentry user = our user id only (no email/username); cleared while logged out.
let sentryUserId: string | undefined
store.subscribe(() => {
    const id = store.getState().userData.userData?.id
    if (id === sentryUserId) return
    sentryUserId = id
    Sentry.setUser(id ? { id } : null)
})

!IS_DEV &&
    Sentry.init({
        dsn: 'https://643de0fa76db4615a1fec0ad6daf5af9@o4504786832326656.ingest.sentry.io/4504808632221696',
        integrations: [Sentry.browserTracingIntegration()],
        environment: ENV,

        // for production, this value should be lower (e.g. 0.25) to lower costs
        // 1 means that 100% of transactions will be sent to Sentry for processing while 0.2 means there is a 20% chance that a transaction will be sent to Sentry
        tracesSampleRate: 0.25,

        // tracesSampler is an alternative to tracesSampleRate that allows you to sample individual transactions based on their context. This is useful if you want to sample only a subset of your traffic, or if you want to sample transactions with different rates. For example:
        // tracesSampler: (samplingContext) => {
        //     if (samplingContext?.transactionContext?.name?.startsWith('ams/')) {
        //         return 0.5
        //     }
        //     return 1.0
        // },

        // Must stay byte-for-byte identical to sentryVitePlugin's release.name in
        // vite.config.ts (both derive from `sentryRelease`), or uploaded
        // sourcemaps never resolve against reported events.
        release: __SENTRY_RELEASE__,

        // Drop known third-party / browser-extension / benign-platform noise
        // before it ever leaves the client.
        ignoreErrors: [
            'Object Not Found Matching Id', // browser-extension postMessage probe
            'Failed to start the audio device', // autoplay policy, pre-gesture — benign
            'The play() request was interrupted', // autoplay policy — benign
            'ResizeObserver loop limit exceeded', // benign layout chatter
            'ResizeObserver loop completed with undelivered notifications',
            /Invalid call to runtime\.sendMessage\(\)\. Tab not found/, // Safari/DuckDuckGo WebExtension
            /DLP was scanning outbound traffic/, // enterprise DLP agent
            /evaluating 'window\.webkit\.messageHandlers/, // in-app browser / translator user scripts
            /^ReferenceError: (?:extDomain is not defined|Can't find variable: extDomain)$/, // injected eval'd script
            /^ReferenceError: Can't find variable: CONFIG$/, // X/Twitter in-app browser user script
        ],
        denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /safari-web-extension:/i, /^iabjs:\/\//i],

        beforeSend(event) {
            // Backstop: in prod this is an ERROR monitor, not a lifecycle log.
            // Drop any info-level event that slips through (the connectors now
            // emit breadcrumbs instead of info captureMessage calls).
            if (IS_PROD && event.level === 'info') {
                return null
            }
            // Page served through a URL-rewriting web proxy, or injected code: frames exist but none is ours (our
            // bundles, the Stockfish worker or its wasm/blob; vendor_sentry's wrapper also wraps foreign listeners).
            const files = (event.exception?.values ?? [])
                .flatMap((v) => v.stacktrace?.frames ?? [])
                .map((f) => f.filename ?? '')
            if (files.some((file) => /\/(?:uv\/service|__av|q\/r|cdn\/w)\//.test(file))) return null
            const origin = window.location.origin
            const isOwn = (file: string) =>
                file.startsWith('wasm:') ||
                file.startsWith(`blob:${origin}/`) ||
                (['/assets/', '/stockfish/', '/src/'].some((dir) => file.startsWith(origin + dir)) &&
                    !file.includes('/vendor_sentry-'))
            if (files.length && !files.some(isOwn)) return null
            return event
        },

        beforeBreadcrumb(breadcrumb) {
            // ensureLogin logs the whole profile (email included).
            if (breadcrumb.category === 'console' && breadcrumb.message?.startsWith('User data loaded:')) return null
            return breadcrumb
        },
    })

// Non-Error throws (AMS { error } payloads, DOM Events) stringify badly —
// "[object Event]", "{}" — so extract something readable for the synthetic Error.
const describeThrown = (value: unknown): string => {
    if (value instanceof Event) return `Event(${value.type})`
    try {
        const json = JSON.stringify(value)
        if (json && json !== '{}') return json
    } catch {
        // Cyclic value — fall through to String().
    }
    return String(value)
}

// Route sharedComponents' caught errors (social login, AMS token renewal,
// guest registration…) into Sentry — the submodule itself stays SDK-agnostic
// and only console-logs for hosts that never register a reporter. Non-Error
// values are wrapped via describeThrown, with a per-message fingerprint so
// all wraps don't collapse into one issue.
setSharedErrorReporter((message, error, context, level, tags) => {
    // Warnings (declined native auth etc.) are expected outcomes — keep them as
    // breadcrumbs rather than minting error issues (SDK v7 has no Sentry.logger).
    if (level === 'warning') {
        Sentry.addBreadcrumb({ category: 'sharedComponents', message, level: 'warning', data: { ...context, ...tags } })
        return
    }
    const isError = error instanceof Error
    Sentry.captureException(isError ? error : new Error(`${message}: ${describeThrown(error)}`), {
        tags: { source: 'sharedComponents', ...tags },
        extra: { message, ...context },
        ...(isError ? {} : { fingerprint: ['shared-error', message] }),
    })
})

// Session-health numbers from sharedComponents (lifetime, aborts, native-store
// health). SDK v7 has no usable metrics product, so they ride along as
// breadcrumbs — attached to whatever event is reported next.
setSharedMetricReporter((kind, name, value, attributes, unit) => {
    Sentry.addBreadcrumb({ category: 'metric', message: name, data: { kind, value, unit, ...attributes } })
})

// Long-lived tags (session id/source) so every later event — including errors
// raised by unrelated features — can be sliced by how the session was established.
setSharedTelemetryContextReporter((tags) => {
    for (const [key, value] of Object.entries(tags)) Sentry.setTag(key, value)
})

// A path starting with '//' (e.g. https://play.chessclub.com//) makes the router's createURL throw on every navigation
if (window.location.pathname.startsWith('//')) {
    window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname.replace(/^\/+/, '/') + window.location.search + window.location.hash,
    )
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path="/" element={<App />} errorElement={<RouteErrorBoundary />}>
            <Route index element={<GameView />} />
            <Route path="analysis/:matchId?" element={<AnalysisView />} />
            <Route path="watch" element={<ObserveView />} />
            <Route
                path="puzzles"
                element={ENABLE_PUZZLE_PRACTICE_V3 ? <PuzzlePracticeView /> : <PuzzlesTrainingViewV2 />}
            />
            <Route path="puzzle-quest" element={<PuzzleQuest />} />
            <Route
                path="daily-puzzle/:date?"
                element={ENABLE_DAILY_PUZZLE_V2 ? <DailyPuzzleViewV2 /> : <DailyPuzzleView />}
            />
            <Route path="profile/:section?" element={<ProfileViewV2 />} />

            {ENABLE_COMPONENTS && (
                <Route
                    path="components/:variant"
                    element={
                        <ThemeProvider theme={theme}>
                            <ComponentsView />
                        </ThemeProvider>
                    }
                />
            )}
            <Route path="*" element={<Navigate to="/" />} />
        </Route>,
    ),
    {
        basename: '/',
    },
)

// WebKit can report AudioContext.currentTime as NaN; howler passes it to setValueAtTime, which throws. A past
// start time means "now", so 0 is equivalent; only calls that would throw (non-finite time) are changed.
if (typeof AudioParam !== 'undefined') {
    const setValueAtTime = AudioParam.prototype.setValueAtTime
    AudioParam.prototype.setValueAtTime = function (value, startTime) {
        return setValueAtTime.call(this, value, isFinite(startTime) ? startTime : 0)
    }
}

store
    .getActions()
    .initUser()
    // A failed boot login must not leave a blank page: mount anyway, GlobalContext retries the login
    .catch((error) => Sentry.captureException(error, { tags: { source: 'bootstrap' } }))
    .then(() => {
        const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
        root.render(
            <React.StrictMode>
                <Sentry.ErrorBoundary fallback={<AppError />}>
                    <StoreProvider store={store}>
                        <DialogsProvider>
                            <RouterProvider router={router} />
                        </DialogsProvider>
                    </StoreProvider>
                </Sentry.ErrorBoundary>
            </React.StrictMode>,
        )
    })

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
